// PhotoCard.jsx

import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';

// Services
import { resolveImageUrl } from 'services/storage/StorageAccessService';

// Hooks
import useGalleryCardLoadGate from 'views/gallery/hooks/useGalleryCardLoadGate';

// Utilities
import { isSketchType } from 'utilities/sketchType';
import resolveMediaAspectRatio from 'views/gallery/utils/resolveMediaAspectRatio';

// CSS
import './PhotoCard.css';

/**
 * PhotoCard
 *
 * Gallery photo thumbnail with reserved aspect ratio and loading shimmer.
 *
 * @param {object} node - Photo node to display.
 * @param {function} [onAspectRatio] - Called with measured width/height ratio.
 * @param {boolean} [reserveAspectRatio=true] - When true, sizes the container from
 *   node metadata (or a measured ratio) to avoid layout jump while loading.
 *
 * @returns {ReactNode} The rendered component.
 */
const PhotoCard = ({ node, onAspectRatio, reserveAspectRatio = true }) => {

    // ----- STATE ----- //

    // Resolved image URL for the <img> src.
    const [imageUrl, setImageUrl] = useState(null);

    // Whether the image has finished loading (or failed).
    const [isLoaded, setIsLoaded] = useState(false);

    // Ratio measured from the loaded image; overrides node metadata when present.
    const [measuredRatio, setMeasuredRatio] = useState(null);

    // ----- MEMOS ----- //

    // Prefer original URL, then feed/grid/thumb derivatives.
    const candidateUrls = useMemo(() => (
        [node?.url, node?.url_feed, node?.url_grid, node?.url_thumb, node?.coverUrl]
            .filter((value, index, arr) => typeof value === 'string' && value.trim() && arr.indexOf(value) === index)
    ), [node?.url, node?.url_feed, node?.url_grid, node?.url_thumb, node?.coverUrl]);

    const coverStoragePath = typeof node?.coverStoragePath === 'string'
        ? node.coverStoragePath.trim()
        : '';
    const storagePath = typeof node?.storagePath === 'string'
        ? node.storagePath.trim()
        : coverStoragePath;
    const isSketch = isSketchType(node?.type);
    const loadKey = `${node?.key || ''}:${candidateUrls.join('|')}:${storagePath}`;

    // Reserve layout from node metadata until the image reports its own ratio.
    const aspectRatio = useMemo(
        () => measuredRatio || resolveMediaAspectRatio(node),
        [measuredRatio, node?.width, node?.height, node?.aspectRatio],
    );

    // ----- HOOKS ----- //

    const { containerRef, shouldLoad } = useGalleryCardLoadGate(loadKey);

    // ----- EFFECTS ----- //

    // Reset paint state when the node or URL set changes.
    useLayoutEffect(() => {
        setIsLoaded(false);
        setImageUrl(null);
        setMeasuredRatio(null);
    }, [node?.key, candidateUrls]);

    // Resolve a displayable URL once the card is near the viewport.
    useEffect(() => {
        let cancelled = false;

        if (!shouldLoad) {
            setImageUrl(null);
            setIsLoaded(false);
            return () => {
                cancelled = true;
            };
        }

        (async () => {
            const nodeKey = node?.key || null;

            for (const rawUrl of candidateUrls) {
                const url = await resolveImageUrl({
                    nodeKey,
                    url: rawUrl,
                    storagePath: null,
                });
                if (cancelled) return;
                if (url) {
                    setImageUrl(url);
                    return;
                }
            }

            if (storagePath) {
                const fallback = await resolveImageUrl({
                    nodeKey,
                    url: null,
                    storagePath,
                });
                if (cancelled) return;
                setImageUrl(fallback || null);
                return;
            }

            setImageUrl(null);
        })();

        return () => {
            cancelled = true;
        };
    }, [shouldLoad, node?.key, storagePath, candidateUrls]);

    if (!node) return null;

    // ----- RENDER ----- //

    return (
        <div
          className={`photo-card-container${isSketch ? ' is-sketch' : ''}`}
          ref={containerRef}
          style={reserveAspectRatio ? {
                aspectRatio: String(aspectRatio),
            } : undefined}
        >

            {/* SHIMMER */}
            {!isLoaded && (
                <div
                  aria-hidden="true"
                  className="photo-card-shimmer"
                />
            )}

            {/* IMAGE */}
            {imageUrl ? (
                <img
                  className="photo-card-image"
                  style={{
                        opacity: isLoaded ? 1 : 0,
                    }}
                  alt={node?.title || 'Photo'}
                  src={imageUrl}
                  draggable={false}
                  onError={() => setIsLoaded(true)}
                  onLoad={() => setIsLoaded(true)}
                  onLoadCapture={(event) => {
                        const el = event?.currentTarget;
                        if (el?.naturalWidth && el?.naturalHeight) {
                            const ratio = el.naturalWidth / el.naturalHeight;
                            setMeasuredRatio(ratio);
                            onAspectRatio?.(node, ratio);
                        }
                    }}
                />
            ) : null}
        </div>
    );
};

// Dev Tools
PhotoCard.displayName = 'PhotoCard';

export default PhotoCard;
